import {useMemo} from 'react';
import {kr, tickLabels} from '../../lib/format.js';
import {niceScale, Grid, useHover, HitBands, Tooltip, Legend} from './primitives.jsx';

const W = 560, H = 300, L = 60, R = 10, T = 10, B = 24;

/**
 * The cash curve, booked and then projected, on ONE line.
 *
 * Outcome and projection share an axis because the question is continuous — "where does
 * the money go from here" — but they must never share an appearance. The booked half is
 * solid, the projected half is dashed, and the boundary carries a rule and a label. That
 * is jabr/CLAUDE.md rule 4 applied across time rather than within a month: the reader
 * has to be able to see where measurement stops without consulting a legend.
 *
 * The zero line is drawn heavier than the other gridlines and the crossing month is
 * marked, because on this chart zero is not a gridline — it is the answer.
 */
export default function CashProjection({rows, crossing}) {
	const {at, enter, leave} = useHover();
	const ticks = useMemo(() => tickLabels(rows), [rows]);
	const g = useMemo(() => {
		const vals = rows.map(r => r.cash ?? 0);
		const sc = niceScale(Math.min(0, ...vals), Math.max(0, ...vals));
		const iw = W - L - R, ih = H - T - B;
		return {
			sc,
			y: v => T + ih - ((v - sc.lo) / (sc.hi - sc.lo)) * ih,
			x: i => L + (iw * (i + 0.5)) / rows.length,
		};
	}, [rows]);

	if (!rows.length) return null;

	// The two halves are drawn as separate polylines that SHARE the boundary point, so
	// the line is continuous while the dash pattern changes exactly at the last booked
	// month. Drawing them as two disjoint segments leaves a visible gap that reads as
	// missing data.
	const lastBooked = rows.findLastIndex(r => !r.projected);
	const point = (r, i) => `${g.x(i)},${g.y(r.cash)}`;
	const booked = rows.slice(0, lastBooked + 1).map(point).join(' ');
	const ahead = rows.slice(lastBooked).map((r, k) => point(r, lastBooked + k)).join(' ');
	const boundary = lastBooked >= 0 && lastBooked < rows.length - 1
		? (g.x(lastBooked) + g.x(lastBooked + 1)) / 2 : null;
	const crossIdx = crossing ? rows.findIndex(r => r.month === crossing) : -1;

	return (
		<div className="chart">
			<div className="chart__head">
				<span className="chart__title">Kassaprognos</span>
				<span className="chart__hint">
					bokförd utgående kassa, därefter framskriven. Den streckade delen säger inte vad
					som kommer att hända — den säger när pengarna tar slut om ingenting ändras.
				</span>
			</div>
			<div className="chart__canvas">
				<svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
					aria-label="Kassa per månad, bokförd och framskriven">
					<Grid {...g.sc} y={g.y} L={L} R={R} W={W} />
					{/* Zero is the answer on this chart, not a gridline. */}
					<line x1={L} x2={W - R} y1={g.y(0)} y2={g.y(0)} stroke="#000" strokeWidth={1.2} />
					{boundary != null && (
						<g>
							<line x1={boundary} x2={boundary} y1={T} y2={H - B} stroke="#8a8a8a"
								strokeWidth={1} strokeDasharray="3 3" />
							<text x={boundary + 4} y={T + 10} className="ax" textAnchor="start">prognos →</text>
						</g>
					)}
					<polyline points={booked} fill="none" stroke="#000" strokeWidth={2.2}
						strokeLinejoin="round" />
					<polyline points={ahead} fill="none" stroke="#000" strokeWidth={1.8}
						strokeDasharray="6 4" strokeLinejoin="round" />
					{crossIdx >= 0 && (
						<g>
							<circle cx={g.x(crossIdx)} cy={g.y(0)} r={4} fill="#fff" stroke="#000" strokeWidth={2} />
							<text x={g.x(crossIdx)} y={g.y(0) - 10} className="axs" textAnchor="middle">
								{rows[crossIdx].label ?? crossing}
							</text>
						</g>
					)}
					{ticks.map((t, i) => (t == null ? null : (
						<text key={rows[i].month} x={g.x(i)} y={H - 8} className="axs" textAnchor="middle">{t}</text>
					)))}
					<HitBands months={rows} x={g.x} W={W} H={H} onEnter={enter} onLeave={leave} />
				</svg>
				<Tooltip months={rows} at={at} x={g.x} W={W}
					title={m => `${m.label ?? m.month}${m.projected ? ' · prognos' : ''}`}
					rows={m => (m.projected ? [
						['Kassa', `${kr(m.cash)} kr`],
						['Kundinbetalningar', `${kr(m.receivable_in)} kr`],
						['Ny försäljning', `${kr(m.sales_in)} kr`],
						['Leverantörsbetalningar', `−${kr(m.payable_out)} kr`],
						['Rörelsekostnad', `−${kr(m.operating_out)} kr`],
						['Netto', `${kr(m.net_cash)} kr`],
					] : [
						['Kassa', `${kr(m.cash)} kr`],
						['Intäkt', `${kr(m.revenue)} kr`],
						['Resultat', `${kr(m.result)} kr`],
						['Kundfordringar', `${kr(m.receivable_open)} kr`],
					])} />
			</div>
			<Legend items={[
				{label: 'Bokförd kassa', dash: null},
				{label: 'Framskriven kassa', dash: '6 4'},
			]} />
		</div>
	);
}
